import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { adminApi } from "../../lib/api";
import { Plus, Loader2, ArrowLeft, Calendar, Eye, Trash2, RefreshCw, Archive } from "lucide-react";

const STATUS_LABELS = {
  draft: "Bozza", published: "Pubblicato", archived: "Archiviato",
  cancelled: "Annullato", postponed: "Rinviato",
};

const StatusBadge = ({ status }) => {
  const color = {
    draft: "bg-slate-100 text-slate-700",
    published: "bg-[#E8F5EC] text-[#0E7C3D]",
    archived: "bg-stone-100 text-stone-500",
    cancelled: "bg-red-50 text-red-700",
    postponed: "bg-amber-50 text-amber-800",
  }[status] || "bg-slate-100 text-slate-700";
  return <span className={`text-[11px] uppercase tracking-[0.14em] font-semibold rounded-full px-2.5 py-1 ${color}`}>{STATUS_LABELS[status] || status}</span>;
};

const fmt = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}/${d.getFullYear()}`;
};

const EventsList = () => {
  const [rows, setRows] = useState([]);
  const [trash, setTrash] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("active");
  const [error, setError] = useState(null);

  const load = () => {
    setLoading(true); setError(null);
    Promise.all([adminApi.listEvents(), adminApi.listEventsTrash().catch(() => ({ data: [] }))])
      .then(([a, t]) => { setRows(a.data || []); setTrash(t.data || []); })
      .catch((e) => setError(e?.response?.data?.detail || "Errore nel caricamento eventi. Verifica che il patch V10 sia stato applicato su Supabase."))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const list = tab === "active" ? rows : trash;

  const onDelete = async (id) => {
    if (!window.confirm("Spostare l'evento nel cestino? (potrai ripristinarlo)")) return;
    try { await adminApi.deleteEvent(id); load(); } catch (e) { alert(e?.response?.data?.detail || "Errore"); }
  };
  const onRestore = async (id) => {
    try { await adminApi.restoreEvent(id); load(); } catch (e) { alert(e?.response?.data?.detail || "Errore"); }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
      <Link to="/admin" className="inline-flex items-center gap-2 text-slate-500 hover:text-[#12964A] mb-6" data-testid="back-to-dashboard"><ArrowLeft className="w-4 h-4" /> Torna alla dashboard</Link>
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500 mb-2">Eventi</div>
          <h1 className="font-display font-bold text-4xl md:text-5xl text-[#1F2937]">Gestione eventi</h1>
          <p className="text-slate-500 mt-2">{rows.length} eventi attivi{trash.length > 0 && ` · ${trash.length} nel cestino`}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={load} className="inline-flex items-center gap-2 border border-[#E5E7EB] rounded-full px-4 py-2.5 text-sm hover:border-slate-400"><RefreshCw className="w-4 h-4" /> Aggiorna</button>
          <Link to="/admin/eventi/nuovo" className="inline-flex items-center gap-2 bg-[#12964A] hover:bg-[#0E7C3D] text-white rounded-full px-5 py-2.5 text-sm transition-colors" data-testid="btn-new-event"><Plus className="w-4 h-4" /> Nuovo evento</Link>
        </div>
      </div>

      <div className="mb-4 flex gap-2">
        <button onClick={() => setTab("active")} className={`px-4 py-2 rounded-full text-sm border ${tab === "active" ? "bg-stone-950 text-white border-stone-950" : "bg-white text-stone-700 border-stone-200 hover:border-stone-400"}`}>
          Eventi attivi ({rows.length})
        </button>
        <button onClick={() => setTab("trash")} className={`px-4 py-2 rounded-full text-sm border inline-flex items-center gap-2 ${tab === "trash" ? "bg-stone-950 text-white border-stone-950" : "bg-white text-stone-700 border-stone-200 hover:border-stone-400"}`}>
          <Archive className="w-3.5 h-3.5" /> Cestino ({trash.length})
        </button>
      </div>

      {error && <div className="mb-6 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3">{error}</div>}

      <div className="bg-white rounded-3xl border border-[#E5E7EB] overflow-hidden">
        {loading ? (
          <div className="p-16 text-center text-slate-500"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>
        ) : list.length === 0 ? (
          <div className="p-16 text-center">
            <Calendar className="w-8 h-8 mx-auto text-stone-400 mb-4" />
            <p className="font-display text-2xl text-stone-950">{tab === "active" ? "Nessun evento ancora." : "Cestino vuoto."}</p>
            {tab === "active" && <p className="text-stone-500 mt-2">Clicca su “Nuovo evento” per iniziare.</p>}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-[0.12em]">
              <tr>
                <th className="text-left px-6 py-3">Titolo</th>
                <th className="text-left px-4 py-3">Data</th>
                <th className="text-left px-4 py-3">Stato</th>
                <th className="text-left px-4 py-3">Priorità</th>
                <th className="text-left px-4 py-3">Categoria</th>
                <th className="text-left px-4 py-3">Views</th>
                <th className="text-right px-6 py-3">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {list.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50/50" data-testid={`event-row-${r.slug}`}>
                  <td className="px-6 py-4">
                    <div className="font-semibold text-stone-900">{r.title}</div>
                    <div className="text-xs text-slate-500">/eventi/{r.slug}</div>
                  </td>
                  <td className="px-4 py-4 text-slate-700">{fmt(r.start_at)}</td>
                  <td className="px-4 py-4"><StatusBadge status={r.status} /></td>
                  <td className="px-4 py-4 text-slate-700">{r.priority === "main" ? "🏆 Principale" : r.priority === "featured" ? "⭐ Evidenza" : "Normale"}</td>
                  <td className="px-4 py-4 text-slate-700">{r.category_label || "—"}</td>
                  <td className="px-4 py-4 text-slate-500">{r.view_count || 0}</td>
                  <td className="px-6 py-4 text-right">
                    <div className="inline-flex items-center gap-2">
                      {tab === "active" ? (
                        <>
                          {r.status === "published" && r.slug && (
                            <a href={`/eventi/${r.slug}`} target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-[#12964A]" title="Vedi pubblico"><Eye className="w-4 h-4" /></a>
                          )}
                          <Link to={`/admin/eventi/${r.id}`} className="bg-stone-900 hover:bg-[#12964A] text-white text-xs rounded-full px-3 py-1.5">Modifica</Link>
                          <button onClick={() => onDelete(r.id)} className="text-red-600 hover:text-red-800" title="Elimina"><Trash2 className="w-4 h-4" /></button>
                        </>
                      ) : (
                        <button onClick={() => onRestore(r.id)} className="bg-[#12964A] hover:bg-[#0E7C3D] text-white text-xs rounded-full px-3 py-1.5">Ripristina</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default EventsList;
